"use client";

import { type MotionValue, useTransform } from "motion/react";
import * as m from "motion/react-m";
import Image from "next/image";

import { DeckVideo } from "./DeckVideo";
import {
  fanOpacity,
  fanPerspective,
  fanScale,
  fanX,
  fanZIndex,
  introProgress,
  introRotateY,
  wrapRank,
} from "./fanMath";
import type { DeckItem } from "./types";

type DeckCardProps = {
  item: DeckItem;
  index: number;
  intro: MotionValue<number>;
  isActive: boolean;
  onEnableSound: () => void;
  onPress: (index: number) => void;
  onVideoEnded: () => void;
  position: MotionValue<number>;
  soundOn: boolean;
  total: number;
};

export function DeckCard({
  item,
  index,
  intro,
  isActive,
  onEnableSound,
  onPress,
  onVideoEnded,
  position,
  soundOn,
  total,
}: DeckCardProps) {
  const rank = useTransform(position, value => wrapRank(index - value, total));
  // Per-card intro progress (0 = stacked on the front slot, 1 = resting fan pose): cards peel off
  // the stack one after another via staggered windows of the linear intro clock.
  const progress = useTransform([rank, intro], ([r, t]: number[]) => introProgress(r, t));
  const x = useTransform([rank, progress], ([r, p]: number[]) => `${fanX(r) * p}%`);
  const scale = useTransform([rank, progress], ([r, p]: number[]) => 1 + (fanScale(r) - 1) * p);
  // The 3D turn lives on a nested element so the perspective projection happens about the card's
  // own centre, before the outer translate/scale places it in the fan (matches the design renders).
  const rotateY = useTransform([rank, progress], ([r, p]: number[]) => introRotateY(r, p));
  const transformPerspective = useTransform(rank, fanPerspective);
  const opacity = useTransform(rank, fanOpacity);
  const zIndex = useTransform(rank, fanZIndex);
  // The settled exit card (rank -1) and the hidden back of the deck are transparent yet sit above
  // the front card via z-index; without this they'd capture taps meant for the front card and its
  // play/pause button. Only cards that are actually visible should be hit-testable.
  const pointerEvents = useTransform(opacity, value => (value > 0.05 ? "auto" : "none"));

  return (
    <m.div
      className='tw-pointer-events-none tw-absolute tw-inset-0 tw-select-none'
      style={{ opacity, scale, x, zIndex }}
    >
      <m.div className='tw-absolute tw-inset-0' style={{ rotateY, transformPerspective }}>
        {/* The motion-driven pointerEvents makes the visible card body hit-testable; reporting the
            press here tells the deck which card a tap landed on (drags still bubble to the deck's
            handlers). Transparent cards stay non-interactive so they can't steal taps. */}
        <m.div
          className='tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-[24px] tw-bg-gray-100'
          onPointerDown={() => onPress(index)}
          style={{ pointerEvents }}
        >
          <Image
            alt={item.name ?? "Avatar"}
            className='tw-object-cover'
            draggable={false}
            fill
            priority={index === 0}
            sizes='(min-width: 1024px) 192px, 176px'
            src={item.imageSrc}
          />

          {/* A lone deck can't auto-advance, so loop its clip instead of stalling on the last frame. */}
          {isActive && item.videoSrc && (
            <DeckVideo
              loop={total === 1}
              onEnableSound={onEnableSound}
              onEnded={onVideoEnded}
              soundOn={soundOn}
              src={item.videoSrc}
            />
          )}
        </m.div>
      </m.div>
    </m.div>
  );
}
